'use client'

import TextInputWrapper from '../TextInputWrapper'
import classNames from 'classnames'
import styles from './Textarea.module.css'
import { TextareaInputProps } from './types'
import { forwardRef, useRef } from 'react'

const Textarea = forwardRef<HTMLTextAreaElement, TextareaInputProps>(function Textarea(
  {
    label,
    name,
    description,
    error,
    warning,
    id,
    required,
    className,
    onChange,
    errorDisplay,
    rows = 1,
    fullWidth,
    ...props
  },
  ref
) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const descriptionIds = []
  if (error) descriptionIds.push(`${name}-error`)
  if (warning) descriptionIds.push(`${name}-warning`)
  if (description) descriptionIds.push(`${name}-description`)

  return (
    <TextInputWrapper
      label={label}
      name={name}
      description={description}
      error={error}
      warning={warning}
      id={id || name}
      required={required}
      errorDisplay={errorDisplay}
      fullWidth={fullWidth}
    >
      {
        /**
         * The following trick is used for the auto resizing textarea: https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas/
         * We need to make sure the same styles are applied to both the grow wrapper and the input
         */
        <div
          className={classNames(styles.growWrap, className, {
            [styles.wrapError!]: !!error,
            ['fullWidth']: fullWidth && fullWidth !== 'mobile-only',
            ['fullWidthOnMobile']: fullWidth && fullWidth === 'mobile-only',
          })}
          ref={wrapperRef}
        >
          <textarea
            {...props}
            ref={ref}
            id={id || name}
            name={name}
            aria-describedby={descriptionIds.length ? descriptionIds.join(' ') : undefined}
            aria-invalid={!!error}
            aria-required={required}
            className={classNames(styles.input, className, {
              [styles['input--error']!]: !!error,
              ['fullWidth']: fullWidth && fullWidth !== 'mobile-only',
              ['fullWidthOnMobile']: fullWidth && fullWidth === 'mobile-only',
            })}
            rows={rows}
            onChange={(e) => {
              wrapperRef.current?.setAttribute('data-replicated-value', e.target.value)
              if (onChange) {
                onChange(e)
              }
            }}
          />
        </div>
      }
    </TextInputWrapper>
  )
})

export default Textarea
